import { forwardRef, type HTMLAttributes } from "react";
import { AVATARS, type AvatarId } from "@jumpbean/shared";

export type AvatarSize = "sm" | "md" | "lg" | "xl";

export interface AvatarProps extends HTMLAttributes<HTMLDivElement> {
  /** Avatar ID from the predefined avatars */
  avatarId: AvatarId;
  /** Size of the avatar */
  size?: AvatarSize;
  /** Child's display name (used for fallback and accessibility) */
  name?: string;
  /** Whether this avatar is currently selected */
  isSelected?: boolean;
  /** Whether this avatar can be clicked/selected */
  isSelectable?: boolean;
  /** Callback when avatar is clicked (when isSelectable) */
  onSelect?: () => void;
}

/**
 * Avatar component for displaying child profile avatars.
 * Uses emoji-based avatars with colorful backgrounds for a friendly appearance.
 *
 * @example
 * // Display avatar for a child profile
 * <Avatar avatarId="bunny" name="Emma" size="lg" />
 *
 * @example
 * // Selectable avatar in profile picker
 * <Avatar
 *   avatarId="fox"
 *   isSelectable
 *   isSelected={selectedAvatar === 'fox'}
 *   onSelect={() => setSelectedAvatar('fox')}
 * />
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      avatarId,
      size = "md",
      name,
      isSelected = false,
      isSelectable = false,
      onSelect,
      className = "",
      ...props
    },
    ref
  ) => {
    const avatar = AVATARS.find((a: typeof AVATARS[number]) => a.id === avatarId);
    const emoji = avatar?.emoji ?? "🐾";
    const avatarName = avatar?.name ?? "Friend";

    // Size configurations
    const sizeStyles = {
      sm: {
        container: "w-12 h-12",
        emoji: "text-2xl",
        ring: "ring-2 ring-offset-1",
      },
      md: {
        container: "w-16 h-16",
        emoji: "text-3xl",
        ring: "ring-3 ring-offset-2",
      },
      lg: {
        container: "w-20 h-20",
        emoji: "text-4xl",
        ring: "ring-4 ring-offset-2",
      },
      xl: {
        container: "w-28 h-28",
        emoji: "text-6xl",
        ring: "ring-4 ring-offset-3",
      },
    };

    // Background colors based on avatar
    const avatarColors: Record<AvatarId, string> = {
      bunny: "bg-gradient-to-br from-fairy-100 to-fairy-200",
      bear: "bg-gradient-to-br from-pixel-light to-[#D4A574]",
      cat: "bg-gradient-to-br from-sunshine-100 to-sunshine-200",
      dog: "bg-gradient-to-br from-sky-100 to-sky-200",
      fox: "bg-gradient-to-br from-pixel-light to-pixel",
      panda: "bg-gradient-to-br from-neutral-100 to-neutral-200",
      lion: "bg-gradient-to-br from-sunshine-200 to-sunshine-400",
      unicorn: "bg-gradient-to-br from-luna-light to-magic-200",
    };

    const currentSize = sizeStyles[size];
    const bgColor = avatarColors[avatarId] ?? "bg-gradient-to-br from-magic-100 to-magic-200";

    const handleClick = () => {
      if (isSelectable && onSelect) {
        onSelect();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isSelectable && onSelect && (event.key === "Enter" || event.key === " ")) {
        event.preventDefault();
        onSelect();
      }
    };

    return (
      <div
        ref={ref}
        role={isSelectable ? "button" : undefined}
        tabIndex={isSelectable ? 0 : undefined}
        aria-label={name ? `${name}'s avatar: ${avatarName}` : `${avatarName} avatar`}
        aria-pressed={isSelectable ? isSelected : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`
          relative
          ${currentSize.container}
          rounded-full
          ${bgColor}
          flex items-center justify-center
          border-[3px] border-cloud
          shadow-[0_4px_12px_rgba(0,0,0,0.1)]
          transition-all duration-200
          ${isSelectable ? "cursor-pointer hover:scale-105 active:scale-95" : ""}
          ${isSelected ? `${currentSize.ring} ring-sunshine-400 ring-offset-cloud` : ""}
          ${isSelectable && !isSelected ? "hover:shadow-[0_6px_16px_rgba(0,0,0,0.15)]" : ""}
          focus:outline-none focus-visible:ring-4 focus-visible:ring-fairy-400 focus-visible:ring-offset-2
          ${className}
        `}
        {...props}
      >
        {/* Emoji avatar */}
        <span className={`${currentSize.emoji} select-none`} role="img" aria-hidden="true">
          {emoji}
        </span>

        {/* Selection checkmark */}
        {isSelected && (
          <div
            className="
              absolute -bottom-1 -right-1
              w-6 h-6
              bg-meadow-400
              rounded-full
              flex items-center justify-center
              border-2 border-cloud
              shadow-sm
              animate-[bounceIn_300ms_cubic-bezier(0.34,1.56,0.64,1)]
            "
          >
            <CheckIcon className="w-3.5 h-3.5 text-white" />
          </div>
        )}

        {/* Subtle floating animation for selected state */}
        {isSelected && (
          <div className="absolute inset-0 rounded-full animate-pulse opacity-20 bg-sunshine-400" />
        )}
      </div>
    );
  }
);

Avatar.displayName = "Avatar";

/** Check mark icon */
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={3}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

// Add bounceIn animation if not present
if (typeof document !== "undefined") {
  const styleId = "jumpbean-avatar-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes bounceIn {
        0% { transform: scale(0); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Avatar;
